import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { CalendarEventAction, CalendarEventTimesChangedEvent, CalendarView } from 'angular-calendar';
import { CalendarEvent, EventColor } from 'calendar-utils';
import { CalendarService } from 'src/app/services/calendar.service';
import { formatDate } from '@angular/common';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import {
  startOfDay,
  endOfDay,
  subDays,
  addDays,
  endOfMonth,
  isSameDay,
  isSameMonth,
  addHours,
} from 'date-fns';
import { Subject } from 'rxjs';
import { ChangeDetectorRef } from '@angular/core';
import { CommonModalComponent } from '@common2/components/modal/modal.component';
import { Location } from '@angular/common';
import { StorageService } from '@services';

const colors: Record<string, EventColor> = {
  red: {
    primary: '#ad2121',
    secondary: '#FAE3E3',
  },
  blue: {
    primary: '#1e90ff',
    secondary: '#D1E8FF',
  },
  yellow: {
    primary: '#e3bc08',
    secondary: '#FDF1BA',
  },
};

@Component({
  selector: 'app-academy-calendar',
  templateUrl: './academy-calendar.component.html',
  styleUrls: ['./academy-calendar.component.scss', "./resources/bootstrap.min.css"],
})
export class AcademyCalendarComponent {
  public errorMessage: string;
  public showModal = false;
  public selectedEvent: any = null;
  public viewStart: string;
  public viewEnd: string;
  public me: any;
  public isLoading: boolean = false;

  @ViewChild('modalContent', { static: true }) modalContent: CommonModalComponent;
  view: CalendarView = CalendarView.Week;
  monthView : CalendarView = CalendarView.Month;
  language: string = 'en';
  CalendarView = CalendarView;
  dateToday = new Date();
  today = format(this.dateToday, 'yyyy-MM-dd'); // año-día-mes

  viewDate: Date = new Date();

  modalData: {
    action: string;
    event: CalendarEvent;
  };

  actions: CalendarEventAction[] = [
    {
      label: '<i class="fas fa-fw fa-pencil-alt"></i>',
      a11yLabel: 'Edit',
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.handleEvent('Edited', event);
      },
    },
    {
      label: '<i class="fas fa-fw fa-trash-alt"></i>',
      a11yLabel: 'Delete',
      onClick: ({ event }: { event: CalendarEvent }): void => {
        this.events = this.events.filter((iEvent) => iEvent !== event);
        this.handleEvent('Deleted', event);
      },
    },
  ];

  refresh = new Subject<void>();

  events: CalendarEvent[] = [];

  activeDayIsOpen: boolean = true;

  constructor(
    private modal: NgbModal, 
    private cdr: ChangeDetectorRef,
    private location: Location,
    private calendarService: CalendarService,
    private storageService: StorageService,
  ) {}

  ngOnInit(): void {
    this.me = this.storageService.get('me');
    this.language = this.me.language.code;
    this.loadCalendarEvents();
  }
  ngAfterViewInit() {
    setTimeout(() => this.cdr.detectChanges(), 100);
  }

  dayClicked({ date, events }: { date: Date; events: CalendarEvent[] }): void {
    if (isSameMonth(date, this.viewDate)) {
      if (
        (isSameDay(this.viewDate, date) && this.activeDayIsOpen === true) ||
        events.length === 0
      ) {
        this.activeDayIsOpen = false;
      } else {
        this.activeDayIsOpen = true;
      }
      this.viewDate = date;
      this.loadCalendarEvents();
    }
  }
  onViewDateChange(date: Date) {
    this.viewDate = date;
    this.loadCalendarEvents()
  }
  updateDateRange() {
    const day = this.viewDate;
    const start = startOfWeek(day, { weekStartsOn: 0 });
    const end = endOfWeek(day, { weekStartsOn: 0 });
    this.viewStart = format(start, 'yyyy-MM-dd');
    this.viewEnd = format(end, 'yyyy-MM-dd');
    console.log("Rango visible:",this.viewStart, this.viewEnd);
  }
  eventTimesChanged({
    event,
    newStart,
    newEnd,
  }: CalendarEventTimesChangedEvent): void {
    this.events = this.events.map((iEvent) => {
      if (iEvent === event) {
        return {
          ...event,
          start: newStart,
          end: newEnd,
        };
      }
      return iEvent;
    });
    this.handleEvent('Dropped or resized', event);
  }

  handleEvent(action: string, event: CalendarEvent): void {
    this.modalData = { event, action };
    this.modal.open(this.modalContent, { size: 'lg' });
  }

  /* addEvent(): void {
    this.events = [
      ...this.events,
      {
        title: 'New event',
        start: startOfDay(new Date()),
        end: endOfDay(new Date()),
        color: colors.red,
        draggable: true,
        resizable: {
          beforeStart: true,
          afterEnd: true,
        },
      },
    ];
  }
 */
  deleteEvent(eventToDelete: CalendarEvent) {
    this.events = this.events.filter((event) => event !== eventToDelete);
  }

  setView(view: CalendarView) {
    this.view = view;
  }

  closeOpenMonthViewDay() {
    this.activeDayIsOpen = false;
  }

  public openModal(): void {
    this.modalContent.onOpen();
  }

  public goBack(): void {
    this.location.back();
  }

  onSidebarDateSelected(date: Date): void {
    this.viewDate = date;
    this.loadCalendarEvents(); // recalcula el rango y hace fetch

    // Opcional: si querés forzar a ir a week-view cada vez
    // this.view = CalendarView.Week;
  }
  public loadCalendarEvents(): void {
    this.isLoading = true;
    this.updateDateRange();
    const startDate = this.viewStart;
    const endDate = this.viewEnd;

    this.calendarService.getCalendarEvents(startDate, endDate).subscribe({
      next: response => {
        this.events = this.mapToCalendarEvents(response.events);
         this.isLoading = false;
      },
      error: err => {
        this.errorMessage = err.message;
        console.error('Error al cargar eventos:', err);
        this.isLoading = false;
      }
    });
  }
  mapToCalendarEvents(apiEvents: any[]): CalendarEvent[] {
    const calendarEvents: CalendarEvent[] = [];
    let actionSet : CalendarEventAction[] =  [];
    apiEvents.forEach(event => {
      if (event.editable === true) {
        actionSet = this.actions;
      }
      if (event.occurrences && event.occurrences.length > 0) {
        event.occurrences.forEach((occurrence: any) => {
          calendarEvents.push({
            title: event.title,
            start: new Date(occurrence.start),
            end: new Date(occurrence.end),
            actions: actionSet,
            meta: {
              originalEvent: event
            },
            draggable: false,
            resizable: {
              beforeStart: false,
              afterEnd: false
            }
          });
        });
      } else {
        const start = new Date(event.startDate);
        const end = new Date(event.endDate);
        calendarEvents.push({
          title: event.title,
          start,
          end,
          actions: actionSet,
          meta: {
            originalEvent: event
          },
          draggable: false,
          resizable: {
            beforeStart: false,
            afterEnd: false
          }
        });
      }
    });

    return calendarEvents;
  }
  

  openNewEvent(): void {
    console.log('👉 Click detectado: abriendo modal');
    this.selectedEvent = null;
    this.showModal = true;
  }

  editEvent(event: any): void {
    this.selectedEvent = event;
    this.showModal = true;
  }

  onSaveEvent(data: any): void {
    this.showModal = false;

    if (this.selectedEvent) {
      this.calendarService.updateEvent(this.selectedEvent.id, data).subscribe(() => this.loadCalendarEvents());
    } else {
      this.calendarService.createEvent(data).subscribe(() => this.loadCalendarEvents());
    }
  }

}
